import { useEffect, useState } from "react";
import { License } from "../models/Licenses/License";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import agent from "../../api/agent";
import { LicenseGenerate } from "../models/Licenses/LicenseGenerate";
import AddCardIcon from "@mui/icons-material/AddCard";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import DownloadIcon from "@mui/icons-material/Download";
import { LicenseCreate } from "../models/Licenses/LicenseCreate";
import DeleteConfirmationBox from "../layout/share_components/DeleteConfirmationBox";
import EditLicenseForm from "../forms/Licenses/EditLicenseForm";
import AddOrEditUserLinkForm from "../forms/Licenses/AddOrEditUserLinkForm";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhonelinkOffIcon from "@mui/icons-material/PhonelinkOff";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import { showToast } from "../layout/share_components/ToastMessage";
import { jwtDecode } from "jwt-decode";

interface Props {
  softwarePrefixName: string | undefined;
  selectedSubscriptionId: number;
  maxPaxNo: number;
  closeDialog: () => void;
  userRole: string;
}

export default function LicenseDetails({
  softwarePrefixName,
  selectedSubscriptionId,
  closeDialog,
  maxPaxNo,
  userRole,
}: Props) {
  const [licenseList, setLicenseList] = useState<License[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editFormOpenState, setEditFormOpenState] = useState<boolean>(false);
  const [deleteDialogOpenState, setDeleteDialogOpenState] =
    useState<boolean>(false);

  // Open state for user linking form
  const [userLinkFormOpenState, setUserLinkFormOpenState] =
    useState<boolean>(false);
  const [defaultUserId, setDefaultUserId] = useState<number | undefined>(
    undefined,
  );

  const [selectedLicense, setSelectedLicense] = useState<LicenseCreate | null>(
    null,
  );
  const [selectedLicenseId, setSelectedLicenseId] = useState<number>(0);
  const [selectedLicenseKey, setSelectedLicenseKey] = useState<string>("");

  // Authentication
  const [token, setToken] = useState<string>("");

  // For fileName
  const [username, setUsername] = useState<string>("");

  // For table sorting
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<"name" | "email">("name");

  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState<"name" | "email">("name");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          if (checkTokenExpiration(tokenFLocal)) {
            return;
          }

          // Decode token to get the username
          const decodedToken: any = jwtDecode(tokenFLocal);

          const username = decodedToken.name;
          const trimmedUsername = username.replace(/\s+/g, "");
          setUsername(trimmedUsername);

          setToken(tokenFLocal);
          const licenseBySubscription =
            await agent.License.getLicenseBySubscription(
              selectedSubscriptionId,
              tokenFLocal,
            );
          setLicenseList(licenseBySubscription);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // For table sorting function
  const handleSort = (property: "name" | "email") => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // 1) First filter
  const filteredLicenseList = licenseList.filter((license) => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();
    const value =
      searchColumn === "name"
        ? (license.userName?.toLowerCase() ?? "")
        : (license.email?.toLowerCase() ?? "");

    return value.includes(term);
  });

  // 2) Then sort the filtered result
  const sortedLicenseList = [...filteredLicenseList].sort((a, b) => {
    const isAsc = order === "asc";

    if (orderBy === "name") {
      // Handle undefined values for name
      const nameA = a.userName?.toLowerCase() || "";
      const nameB = b.userName?.toLowerCase() || "";
      return isAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    } else {
      // Handle undefined values for email
      const emailA = a.email?.toLowerCase() || "";
      const emailB = b.email?.toLowerCase() || "";
      return isAsc
        ? emailA.localeCompare(emailB)
        : emailB.localeCompare(emailA);
    }
  });

  const handleGenerateLicense = async () => {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    const licenseData: LicenseGenerate = {
      subscriptionId: selectedSubscriptionId,
    };

    try {
      // Get the date format in "MMDDYY" format
      const formattedDate = new Date()
        .toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        })
        .replace(/\//g, "");
      const response = await agent.License.generateNewLicense(
        licenseData,
        token,
      );

      // Create download link for the blob
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${softwarePrefixName}_license_${username}_${formattedDate}.lic`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      // refetching latest license from DB
      const licenseBySubscription =
        await agent.License.getLicenseBySubscription(
          selectedSubscriptionId,
          token,
        );
      setLicenseList(licenseBySubscription);

      setLoading(false);
    }
  };

  const handleLicenseDownloadOnClick = async (licenseKey: string) => {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (!licenseKey) return;
    setLoading(true);
    try {
      // Get the date format in "MMDDYY" format
      const formattedDate = new Date()
        .toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        })
        .replace(/\//g, "");
      const response = await agent.License.downloadLicense(licenseKey, token);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${softwarePrefixName}_license_${username}_${formattedDate}.lic`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
      alert("License download failed. Please check the License Key.");
    } finally {
      setLoading(false);
    }
  };

  function handleEditFormOpen(licenseId: number) {
    const getLicense = licenseList.find((x) => x.licenseId === licenseId);
    if (getLicense) {
      // Map License to LicenseCreate
      const LicenseCreate: LicenseCreate = {
        startDate: getLicense.startDate,
        validDate: getLicense.validDate,
        subscriptionId: getLicense.subscriptionId,
      };
      setSelectedLicense(LicenseCreate);
      setSelectedLicenseId(licenseId);
      setEditFormOpenState(true);
    }
  }

  function handleEditFormClose() {
    setEditFormOpenState(false);
    setSelectedLicense(null);
    setSelectedLicenseId(0);
  }

  function handleDeleteOnClick(licenseId: number) {
    setSelectedLicenseId(licenseId);
    setDeleteDialogOpenState(true);
  }

  function handleDeleteDialogClose() {
    setSelectedLicenseId(0);
    setDeleteDialogOpenState(false);
  }

  // Function to update License Details
  async function handleEditSubmit(formData: LicenseCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.License.update(selectedLicenseId, formData, token).then(
        async () => {
          const updatedLicense = await agent.License.getLicenseBySubscription(
            selectedSubscriptionId,
            token,
          );
          setLicenseList(updatedLicense);
          handleEditFormClose();
          showToast("License Details Edited Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error Updating License Record:", error);
      showToast("Error Updating license details", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function to Delete License record
  async function handleConfirmDelete() {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (selectedLicenseId === 0) return;
    setLoading(true);
    try {
      await agent.License.delete(selectedLicenseId, token).then(async () => {
        const updatedLicense = await agent.License.getLicenseBySubscription(
          selectedSubscriptionId,
          token,
        );
        setLicenseList(updatedLicense);
        handleDeleteDialogClose();
        showToast("License Deleted Successfully", "success");
      });
    } catch (error) {
      console.error("Error Deleting License Record:", error);
      showToast("Error deleting License", "error");
    } finally {
      setLoading(false);
      handleDeleteDialogClose();
    }
  }

  // Function to open the user link form
  function handleUserLinkFormOpen(licenseKey: string) {
    const getLicense = licenseList.find((x) => x.licenseKey === licenseKey);
    if (getLicense) {
      setSelectedLicenseKey(licenseKey);
      setDefaultUserId(getLicense.userId);
      setUserLinkFormOpenState(true);
    }
  }

  // Function to close the user link form
  function handleUserLinkFormClose() {
    setUserLinkFormOpenState(false);
    setSelectedLicenseKey("");
  }

  // Function to handle the user link submit function
  async function handleUserLinkSubmit(userId: number | undefined) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.License.assignUserToLic(
        selectedLicenseKey,
        userId,
        token,
      ).then(async () => {
        const updatedLicense = await agent.License.getLicenseBySubscription(
          selectedSubscriptionId,
          token,
        );
        setLicenseList(updatedLicense);
        // handleUserLinkFormClose();

        if (userId === undefined) {
          showToast("User Unassigned Successfully", "success");
        } else {
          showToast("User Linked Successfully", "success");
        }
      });
    } catch (error) {
      console.error("Error Updating License Record:", error);
      showToast("User Linked Unsuccessful", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function to clear the linked pc and change the status
  async function handleClearLinkedPcOnClick(licenseKey: string) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.License.clearLinkedPc(licenseKey, token).then(async () => {
        const updatedLicense = await agent.License.getLicenseBySubscription(
          selectedSubscriptionId,
          token,
        );
        setLicenseList(updatedLicense);
        showToast("Linked PC Clear Successfully", "success");
      });
    } catch (error) {
      console.error("Error remove the pc:", error);
      showToast("Error remove the PC", "error");
    } finally {
      setLoading(false);
    }
  }

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading Data..." />;
  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        {licenseList.length < maxPaxNo && userRole === "Super Admin" ? (
          // Button to Generate new License details with lic file
          <Tooltip title="Generate New License">
            <IconButton onClick={() => handleGenerateLicense()}>
              <AddCardIcon />
            </IconButton>
          </Tooltip>
        ) : null}

        <TextField
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FormControl sx={{ ml: 1 }}>
          <InputLabel>Filter by</InputLabel>
          <Select
            value={searchColumn}
            label="Filter by"
            onChange={(e) =>
              setSearchColumn(e.target.value as "name" | "email")
            }
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {sortedLicenseList.length === 0 ? (
        <Box sx={{ mt: 5 }}>
          <Divider>
            <Typography variant="button">No Record found</Typography>
          </Divider>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {userRole === "Super Admin" ? (
                    <TableCell>License Key</TableCell>
                  ) : (
                    <TableCell>No</TableCell>
                  )}
                  <TableCell>Effective Date</TableCell>
                  <TableCell>Valid Date</TableCell>
                  <TableCell>Status</TableCell>
                  {userRole === "Super Admin" ? (
                    <TableCell>Hard Drive Serial Number</TableCell>
                  ) : null}
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "email"}
                      direction={orderBy === "email" ? order : "asc"}
                      onClick={() => handleSort("email")}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLicenseList.map((license, index) => (
                  <TableRow key={license.licenseId}>
                    {userRole === "Super Admin" ? (
                      <TableCell>{license.licenseKey}</TableCell>
                    ) : (
                      <TableCell>{index + 1}</TableCell>
                    )}

                    <TableCell>{license.startDate}</TableCell>
                    <TableCell>{license.validDate}</TableCell>
                    <TableCell>
                      {license.status === "Used" ? (
                        <Chip label="Used" color="success" variant="outlined" />
                      ) : (
                        <Chip label="Unused" color="error" variant="outlined" />
                      )}
                    </TableCell>
                    {userRole === "Super Admin" ? (
                      <TableCell>{license.hardDriveSerial}</TableCell>
                    ) : null}

                    <TableCell>{license.userName}</TableCell>
                    <TableCell>{license.email}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {/* Button to redownload the lic file */}
                        <Tooltip title="Download License File">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleLicenseDownloadOnClick(license.licenseKey)
                            }
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Link Email / Assign License">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleUserLinkFormOpen(license.licenseKey)
                            }
                          >
                            <PersonAddIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Remove the Linking">
                          <IconButton
                            size="small"
                            disabled={license.status === "Unused"}
                            onClick={() =>
                              handleClearLinkedPcOnClick(license.licenseKey)
                            }
                          >
                            <PhonelinkOffIcon />
                          </IconButton>
                        </Tooltip>

                        {userRole === "Super Admin" && (
                          <>
                            <Tooltip title="Edit License Details">
                              <IconButton
                                color="success"
                                size="small"
                                onClick={() =>
                                  handleEditFormOpen(license.licenseId)
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete License">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleDeleteOnClick(license.licenseId)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
        <Button variant="outlined" onClick={closeDialog}>
          close
        </Button>
      </Box>

      {/* --------------------- Form MODEL------------------------------- */}
      {/* Model for License edit form */}
      <Dialog
        open={editFormOpenState}
        onClose={handleEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Edit License Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedLicense && (
            <EditLicenseForm
              selectedLicense={selectedLicense}
              closeForm={handleEditFormClose}
              edit={handleEditSubmit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Model for user Linking form */}
      <Dialog
        open={userLinkFormOpenState}
        onClose={handleUserLinkFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Link User To License
          </Typography>
        </DialogTitle>
        <DialogContent>
          <AddOrEditUserLinkForm
            defaultUserId={defaultUserId}
            closeForm={handleUserLinkFormClose}
            userLinkSubmit={handleUserLinkSubmit}
            userRole={userRole}
            subscriptionId={selectedSubscriptionId}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationBox
        open={deleteDialogOpenState}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}
